import styled from '@emotion/styled';
import { motion } from 'framer-motion';

export const CookieBanner = styled(motion.div)<{ colors: any }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const CookieBannerContent = styled(motion.div)<{ colors: any }>`
  background: ${props => props.colors.bg};
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 768px) {
    padding: 20px;
    max-width: 95%;
  }
  
  @media (max-width: 480px) {
    padding: 15px;
    max-width: 100%;
    border-radius: 8px;
    gap: 15px;
  }
`;

export const CookieBannerTitle = styled.h2<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.2rem, 2vw, 1.8rem);
  color: ${props => props.colors.text};
  margin: 0 0 10px 0;
`;

export const CookieBannerText = styled.p<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.85rem, 1.2vw, 1rem);
  color: ${props => props.colors.text};
  margin: 0;
  line-height: 1.6;
  
  a {
    color: ${props => props.colors.primary};
    text-decoration: underline;
    
    &:hover {
      color: ${props => props.colors.primaryHover};
    }
  }
`;

export const CookieBannerButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: 10px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
`;

export const CookieButton = styled.button<{ colors: any; variant?: 'primary' | 'secondary' }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.85rem, 1.2vw, 1rem);
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${(props: { colors: any; variant?: 'primary' | 'secondary' }) => props.variant === 'primary' ? `
    background: ${props.colors.primary};
    color: white;
    
    &:hover {
      background: ${props.colors.primaryHover};
    }
  ` : `
    background: transparent;
    color: ${props.colors.primary};
    border: 2px solid ${props.colors.primary};
    
    &:hover {
      background: ${props.colors.primary};
      color: white;
    }
  `}
`;

export const CookieModal = styled(motion.div)<{ colors: any }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const CookieModalContent = styled(motion.div)<{ colors: any }>`
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    max-width: 95%;
    max-height: 85vh;
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
    border-radius: 8px;
  }
`;

export const PanelHeader = styled.div<{ colors: any }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 2px solid #e5e7eb;
  background: ${props => props.colors.primary};
`;

export const HeaderTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-size: 1.8rem;
  font-weight: bold;
  z-index: 100000;
  pointer-events: auto;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
    padding: 10px 12px;
  }
`;

export const CookieModalTitle = styled.h2<{ colors: any }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.2rem, 2vw, 1.8rem);
  color: ${props => props.colors.text};
  margin: 0 0 20px 0;
`;

export const CookieCategoryHeader = styled.div<{ isOpen: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.isOpen ? '10px' : '0'};
  gap: 15px;
  cursor: pointer;
  user-select: none;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  > div {
    flex: 1;
    min-width: 0;
  }
`;

export const CategoryToggleIcon = styled.button<{ isOpen: boolean; colors: any }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${props => props.colors.primary}20;
  color: ${props => props.colors.primary};
  transition: transform 0.3s ease;
  transform: rotate(${props => props.isOpen ? '180deg' : '0deg'});
  flex-shrink: 0;
  border: none;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    background: ${props => props.colors.primary}30;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    position: absolute;
    top: 0;
    right: 0;
  }
`;

export const CookieCategoryContent = styled(motion.div)`
  overflow: hidden;
`;

export const CookieCategory = styled.div<{ colors: any }>`
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${props => props.colors.border || '#e0e0e0'};
  position: relative;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

export const CookieCategoryTitle = styled.h3<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1rem, 1.5vw, 1.3rem);
  color: ${props => props.colors.text};
  margin: 0;
`;

export const CookieCategoryDescription = styled.p<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.85rem, 1.2vw, 1rem);
  color: ${props => props.colors.textSecondary};
  margin: 0 0 10px 0;
  line-height: 1.6;
`;

export const CookieList = styled.ul<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.8rem, 1.1vw, 0.9rem);
  color: ${props => props.colors.textSecondary};
  margin: 10px 0 0 0;
  padding-left: 0;
  line-height: 1.8;
  list-style: none;
  
  li {
    margin-bottom: 12px;
    padding: 10px;
    background: ${props => props.colors.bg};
    border-left: 3px solid ${props => props.colors.primary};
    border-radius: 4px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  code {
    background: ${props => props.colors.bg};
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    color: ${props => props.colors.primary};
    border: 1px solid ${props => props.colors.border || '#e0e0e0'};
    font-weight: 600;
  }
`;

export const CookieStats = styled.div<{ colors: any }>`
  margin-top: 20px;
  margin-bottom: 30px;
  padding: 15px;
  background: ${props => props.colors.bg};
  border: 1px solid ${props => props.colors.border || '#e0e0e0'};
  border-radius: 8px;
  
  h4 {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(0.9rem, 1.3vw, 1.1rem);
    color: ${props => props.colors.text};
    margin: 0 0 10px 0;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 10px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

export const StatItem = styled.div<{ colors: any }>`
  text-align: center;
  padding: 10px;
  background: ${props => props.colors.bg};
  border-radius: 6px;
  
  .stat-value,
  p.stat-value {
    font-size: clamp(1.5rem, 2.5vw, 2rem);
    font-weight: 700;
    color: ${props => props.colors.primary};
    margin-bottom: 4px;
    margin-top: 0;
  }
  
  .stat-label,
  p.stat-label {
    font-size: clamp(0.75rem, 1vw, 0.85rem);
    color: ${props => props.colors.textSecondary};
    margin-top: 0;
    margin-bottom: 0;
  }
`;

export const ToggleSwitch = styled.label<{ colors: any; disabled?: boolean }>`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  flex-shrink: 0;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
    z-index: 1;
    
    &:checked + span {
      background-color: ${props => props.colors.primary};
      
      &:before {
        transform: translateX(24px);
      }
    }
    
    &:not(:checked) + span {
      background-color: #ccc;
      
      &:before {
        transform: translateX(0);
      }
    }
    
    &:focus-visible {
      outline: none;
    }
    
    &:focus-visible + span {
      outline: 3px solid #00d9ff;
      outline-offset: 2px;
      box-shadow: 0 0 0 3px rgba(0, 217, 255, 0.3), 0 0 0 6px rgba(255, 255, 255, 0.8);
    }
  }
  
  span {
    position: absolute;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.3s;
    border-radius: 26px;
    
    &:before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 24px;
    
    input:checked + span:before {
      transform: translateX(20px);
    }
    
    span:before {
      height: 18px;
      width: 18px;
    }
  }
`;

export const CookieModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
`;

